import React, { useState, useEffect } from 'react';
import { 
  FolderSync, HardDrive, FileText, Database, Shield, Cpu, 
  Terminal, Sparkles, Plus, Trash2, Download, Save, RefreshCw, 
  Grid, List, FileSpreadsheet, PlaySquare, ArrowRight, Share2, 
  Coins, CheckCircle2, Lock, ShieldAlert, Edit3, HelpCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { NexoraXSortDropdown } from './NexoraXSortDropdown';
import { authenticatedFetch } from './direct-delivery/authenticatedApiHelper';

interface FileItem {
  id: string;
  name: string;
  size: string;
  type: 'docs' | 'sheets' | 'slides' | 'sec';
  department: string;
  lastSynced: string;
  content: string;
  isLocked: boolean;
  meta: string;
  nodePriority?: 'High' | 'Medium' | 'Low';
  lastModified?: number;
}

export function NexoraxWorkspace() {
  const [activeSubApp, setActiveSubApp] = useState<'drive' | 'docs' | 'sheets' | 'slides'>('drive');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentRole, setCurrentRole] = useState<'visitor' | 'client' | 'admin' | 'superadmin'>('admin');
  const [sortBy, setSortBy] = useState<'name' | 'lastModified' | 'priority'>('lastModified');
  
  // Quick Task Modal States
  const [isQuickTaskModalOpen, setIsQuickTaskModalOpen] = useState(false);
  const [qTaskTitle, setQTaskTitle] = useState('');
  const [qTaskDesc, setQTaskDesc] = useState('');
  const [qTaskPriority, setQTaskPriority] = useState('MEDIUM');
  const [qTaskCategory, setQTaskCategory] = useState('CORE');
  const [qTaskDueDate, setQTaskDueDate] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  });
  const [qTaskHook, setQTaskHook] = useState('echo "System action hook initiated"');
  const [qTaskHardware, setQTaskHardware] = useState('Cores 0-1');
  const [qTaskTelemetry, setQTaskTelemetry] = useState(50.0);
  const [isSubmittingQTask, setIsSubmittingQTask] = useState(false);
  const [qTaskSuccessMessage, setQTaskSuccessMessage] = useState('');
  const [qTaskErrorMessage, setQTaskErrorMessage] = useState('');
  
  // Praise sequence
  const praiseText = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Initial State-of-the-Art Mock documents
  const [files, setFiles] = useState<FileItem[]>([
    {
      id: 'doc-1',
      name: 'NexoraX_Service_Overview.pdf',
      size: '420 KB',
      type: 'docs',
      department: 'Public resource',
      lastSynced: 'Just Now',
      content: `**NexoraX Service Overview**\n\nThis document outlines our company services and core digital transformation solutions. We provide high-velocity automation, custom workflow engineering, and public-safe cloud integrations to optimize organization performance.`,
      isLocked: true,
      meta: 'Company services and solution overview',
      nodePriority: 'Medium',
      lastModified: Date.now() - 300000
    },
    {
      id: 'doc-2',
      name: 'Nexus_Search_Product_Guide.pdf',
      size: '1.5 MB',
      type: 'docs',
      department: 'Product guide',
      lastSynced: 'Just Now',
      content: `**Nexus Search Product Guide**\n\nDiscover how to leverage Nexus Search credits for modern business intelligence and discovery. This guide tracks onboarding checkpoints, keyword indexing, and live public telemetry tools.`,
      isLocked: true,
      meta: 'Search credits and business discovery guide',
      nodePriority: 'High',
      lastModified: Date.now()
    },
    {
      id: 'sheet-1',
      name: 'Nexus_Hosting_Setup_Checklist.pdf',
      size: '85 KB',
      type: 'sheets',
      department: 'Client guide',
      lastSynced: '2 mins ago',
      content: JSON.stringify({
        rows: [
          ['Setup Phase', 'Status', 'Responsible Team'],
          ['Domain Procurement', 'Completed', 'NexoraX Core Node'],
          ['SSL Installation', 'Active', 'Global Security Proxy'],
          ['Static Asset CDN Deployment', 'Completed', 'Infrastructure Team'],
          ['Dev Host Routing', 'Verified', 'Vanguard Node']
        ]
      }),
      isLocked: true,
      meta: 'Hosting, SSL, and domain setup checklist',
      nodePriority: 'High',
      lastModified: Date.now() - 120000
    },
    {
      id: 'slide-1',
      name: 'Nexus_Galaxy_Lab_Starter_Guide.pdf',
      size: '2.4 MB',
      type: 'slides',
      department: 'User guide',
      lastSynced: '10 mins ago',
      content: JSON.stringify([
        { title: 'Welcome to Galaxy Lab', draft: 'Introduce modular sandboxes and collaborative workspaces.' },
        { title: 'Getting Started', draft: 'Initialize your system, select your local widgets, and start prototyping.' }
      ]),
      isLocked: false,
      meta: 'Free AI workspace introduction',
      nodePriority: 'Low',
      lastModified: Date.now() - 600000
    },
    {
      id: 'doc-3',
      name: 'NexoraX_Privacy_Consent_Readiness.pdf',
      size: '180 KB',
      type: 'docs',
      department: 'Compliance guide',
      lastSynced: '1 hour ago',
      content: `**NexoraX Privacy & Consent Readiness Summary**\n\nActive compliance reporting for global privacy frameworks. This report certifies that all public-facing pages, interactive components, and client tools adhere to strict client consent, secure data isolation, and preview safety standards.`,
      isLocked: false,
      meta: 'Privacy, consent, and public-preview readiness summary',
      nodePriority: 'Medium',
      lastModified: Date.now() - 3600000
    },
    {
      id: 'doc-4',
      name: 'NexoraX_Public_Preview_Launch_Checklist.pdf',
      size: '95 KB',
      type: 'docs',
      department: 'Launch checklist',
      lastSynced: '3 hours ago',
      content: `**NexoraX Public Preview Launch Checklist**\n\nFinal steps to ensure absolute parity and quality assurance across public interfaces. Checks include link verifications, mobile responsiveness, layout spacing checks, and asset cleanups.`,
      isLocked: false,
      meta: 'Final launch QA and public page checklist',
      nodePriority: 'High',
      lastModified: Date.now() - 10800000
    }
  ]);

  const [selectedFile, setSelectedFile] = useState<FileItem | null>(files[0]);
  const [editorContent, setEditorContent] = useState(files[0]?.content || '');
  const [newFileName, setNewFileName] = useState('');
  const [isSynchingActive, setIsSynchingActive] = useState(false);
  const [syncStatusText, setSyncStatusText] = useState('Workspace resources synced for public preview.');

  const displayedFiles = files.filter(f => {
    if (currentRole === 'visitor') {
      return f.department === 'Public resource' || f.department === 'Product guide' || f.department === 'Launch checklist' || f.department === 'User guide';
    }
    return true; // client, admin, and superadmin see all preview catalogs
  });

  const priorityOrder: Record<string, number> = { 'High': 3, 'Medium': 2, 'Low': 1 };

  const sortedFiles = [...displayedFiles].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'lastModified') {
      const aTime = a.lastModified || 0;
      const bTime = b.lastModified || 0;
      return bTime - aTime;
    } else if (sortBy === 'priority') {
      const aPri = priorityOrder[a.nodePriority || 'Medium'];
      const bPri = priorityOrder[b.nodePriority || 'Medium'];
      if (aPri !== bPri) {
        return bPri - aPri;
      }
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  // Trigger sync effect
  const triggerManualSync = () => {
    setIsSynchingActive(true);
    setSyncStatusText('Processing multi-dimensional verification scans...');
    
    setTimeout(() => {
      setIsSynchingActive(false);
      setSyncStatusText('Workspace Synced successfully to Google Drive & Local Node-02 Storage.');
    }, 1500);
  };

  // ----------------------------------------------------
  // ORACLE DOCS: Word / Google Docs editor logic
  // ----------------------------------------------------
  const handleDocEditChange = (val: string) => {
    setEditorContent(val);
    if (selectedFile) {
      setFiles(prev => prev.map(f => f.id === selectedFile.id ? { ...f, content: val, lastSynced: 'Just edited' } : f));
    }
  };

  const executeNexoraXtyAutogen = () => {
    if (!selectedFile) return;
    const autogenText = `\n\n[NEXORAX COMPLIANCE MANDATE INJECTED - OFFICE OF THE DIRECTOR]\nIn the name of Allah, the Greatest and Most Merciful. This file is verified under Private Ltd SC887095 guidelines.\nAll billing routes to Secure Payment Provider (Sort Code: 04-**-** | Account Number: ******15) containing the 20% 10% Maintenance & Hosting Fee levy structure. Dial 08000 622827 for critical communications access.`;
    handleDocEditChange(editorContent + autogenText);
  };

  // ----------------------------------------------------
  // CHRONOS SHEETS: Excel / Google Sheets logic
  // ----------------------------------------------------
  const [parsedSheetData, setParsedSheetData] = useState<string[][]>([]);
  
  useEffect(() => {
    if (selectedFile && selectedFile.type === 'sheets') {
      try {
        const parsed = JSON.parse(selectedFile.content);
        if (parsed.rows) {
          setParsedSheetData(parsed.rows);
        }
      } catch (err) {
        setParsedSheetData([['Error loading spreadsheet matrix']]);
      }
    }
  }, [selectedFile]);

  const handleCellChange = (rowIndex: number, colIndex: number, newValue: string) => {
    const updated = [...parsedSheetData];
    updated[rowIndex][colIndex] = newValue;
    setParsedSheetData(updated);

    if (selectedFile) {
      const serialized = JSON.stringify({ rows: updated });
      setFiles(prev => prev.map(f => f.id === selectedFile.id ? { ...f, content: serialized, lastSynced: 'Cell updated' } : f));
    }
  };

  // ----------------------------------------------------
  // EMPIRE SLIDES: Presenter Deck logic
  // ----------------------------------------------------
  const [slides, setSlides] = useState<{ title: string; draft: string }[]>([]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  useEffect(() => {
    if (selectedFile && selectedFile.type === 'slides') {
      try {
        const parsed = JSON.parse(selectedFile.content);
        setSlides(parsed);
      } catch (e) {
        setSlides([]);
      }
    }
  }, [selectedFile]);

  const handleSlideContentChange = (index: number, key: 'title' | 'draft', val: string) => {
    const updated = [...slides];
    updated[index][key] = val;
    setSlides(updated);

    if (selectedFile) {
      const serialized = JSON.stringify(updated);
      setFiles(prev => prev.map(f => f.id === selectedFile.id ? { ...f, content: serialized, lastSynced: 'Slide altered' } : f));
    }
  };

  // ----------------------------------------------------
  // DRIVE FILES: New File Creation
  // ----------------------------------------------------
  const createNewFileInDrive = (type: 'docs' | 'sheets' | 'slides') => {
    if (!newFileName.trim()) return;
    
    let defaultPayload = "";
    if (type === 'docs') {
      defaultPayload = `In the name of Allah, the Greatest and Most Merciful.\n\nNexoraX Brief for ${newFileName}\n\nDraft content compiled with 100x efficiency guidelines.`;
    } else if (type === 'sheets') {
      defaultPayload = JSON.stringify({
        rows: [
          ['Transaction Name', 'Value / Gross Harvest', 'Allocation Grid'],
          ['Example Operational Inflow', '£100,000', 'Secured SecurePayment Account'],
          ['SecurePayment Sort Balance', '04-**-**', '******15 Account Tally'],
          ['1% Perpetual NexoraX split', '£1,000', 'Charity Node-01']
        ]
      });
    } else {
      defaultPayload = JSON.stringify([
        { title: 'The NexoraX Frontier', draft: 'A multi-node global empire driven by high-speed visual tools.' },
        { title: 'Strategic Revenue Target', draft: 'Fulfilling the Wednesday liquidity limit at 100% conversion rates.' }
      ]);
    }

    const newFile: FileItem = {
      id: `file-${Date.now()}`,
      name: `${newFileName.trim()}.${type}`,
      size: '22 KB',
      type: type,
      department: 'NexoraX Core Sandbox Workspace',
      lastSynced: 'Just Now',
      content: defaultPayload,
      isLocked: false,
      meta: 'Autonomous custom template',
      nodePriority: type === 'docs' ? 'High' : type === 'sheets' ? 'Medium' : 'Low',
      lastModified: Date.now()
    };

    setFiles(prev => [newFile, ...prev]);
    setSelectedFile(newFile);
    setEditorContent(defaultPayload);
    setNewFileName('');
    setActiveSubApp(type);
  };

  const deleteFile = (id: string) => {
    const target = files.find(f => f.id === id);
    if (!target) return;
    if (target.isLocked) {
      alert("This is a verified public resource and cannot be deleted.");
      return;
    }
    setFiles(prev => prev.filter(f => f.id !== id));
    if (selectedFile?.id === id) {
      setSelectedFile(files[0] || null);
    }
  };

  const handleQuickTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!qTaskTitle.trim()) {
      setQTaskErrorMessage("Task Subject title is required.");
      return;
    }

    setIsSubmittingQTask(true);
    setQTaskErrorMessage('');

    const payload = {
      title: qTaskTitle.trim(),
      description: qTaskDesc.trim() || "No detailed parameters supplied.",
      due_date: qTaskDueDate,
      priority: qTaskPriority,
      category: qTaskCategory.toUpperCase(),
      is_completed: false,
      action_hook_script: qTaskHook.trim() || 'echo "System action hook initiated"',
      hardware_core_allocation: qTaskHardware.trim() || 'Cores 0-1',
      telemetry_load_pct: Number(qTaskTelemetry) || 0.0
    };

    try {
      const data = await authenticatedFetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (data.status === "SUCCESS") {
        setQTaskSuccessMessage(`PAYLOAD_OK // Node registered: ${data.new_id || 'NX-TASK-3D'}`);
        setTimeout(() => {
          setIsQuickTaskModalOpen(false);
          setQTaskSuccessMessage('');
          setQTaskTitle('');
          setQTaskDesc('');
        }, 1600);
      } else {
        setQTaskErrorMessage(data.message || "Failed to submit node payload");
      }
    } catch (err: any) {
      console.error("Failed to add task to SQLite:", err);
      setQTaskErrorMessage("Network transmission failure: SQLite daemon unreachable.");
    } finally {
      setIsSubmittingQTask(false);
    }
  };

  return (
    <div id="nexorax-workspace-suite-root" className="w-full min-h-[700px] bg-[#020510] border-2 border-indigo-950/80 rounded-3xl p-6 relative overflow-hidden text-left flex flex-col gap-6">
      
      {/* Dynamic Grid Background Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0b0f19_1px,transparent_1px),linear-gradient(to_bottom,#0b0f19_1px,transparent_1px)] bg-[size:30px_30px] opacity-40 pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-indigo-600/10 rounded-full filter blur-[150px] pointer-events-none animate-pulse" />

      {/* NexoraX header and synchronization tracker */}
      <div className="relative z-10 flex flex-col xl:flex-row items-stretch xl:items-center justify-between border-b border-white/5 pb-4 gap-4">
        <div>
          <p className="text-[9.5px] font-mono font-bold text-slate-400 tracking-wide block italic mb-1">
            {praiseText}
          </p>
          <div className="flex items-center gap-2">
            <FolderSync className="w-5 h-5 text-[#e840f3] animate-pulse" />
            <h2 className="text-lg font-sans font-black uppercase text-white tracking-widest flex items-center gap-2">
              NEXORAX WORKSPACE SUITE <span className="text-[10px] bg-gradient-to-r from-indigo-500 to-cyan-500 text-black font-black px-2 py-0.5 rounded-full uppercase">100X OVER OFFICE 365</span>
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Indicator */}
          <div className="bg-black/60 border border-emerald-500/25 px-3 py-1.5 rounded-xl flex items-center gap-2 text-[9px] font-mono text-slate-300">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
            <span>Platform status: <strong className="text-emerald-400">PREVIEW SYNCED</strong></span>
          </div>

          <button 
            onClick={triggerManualSync}
            className="px-3.5 py-1.5 bg-indigo-950/60 hover:bg-indigo-900 border border-indigo-500/30 text-[9.5px] font-mono text-cyan-300 rounded-xl cursor-pointer flex items-center gap-1.5 transition-all"
          >
            {isSynchingActive ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FolderSync className="w-3.5 h-3.5" />}
            SYSTEM LEDGER SYNC
          </button>
        </div>
      </div>

      {/* Role Visibility Simulator */}
      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between bg-indigo-950/20 border border-indigo-900/30 p-4 rounded-2xl gap-3 text-left">
        <div className="space-y-1">
          <span className="text-[9px] bg-[#e840f3]/20 text-[#f574fe] border border-[#e840f3]/30 px-2 py-0.5 rounded-full uppercase font-mono font-bold tracking-wider">Role View Matrix Controller</span>
          <p className="text-[10px] font-sans text-slate-400">Select active roles below to simulate resource visibility and administrative configurations dynamically.</p>
        </div>
        <div className="flex flex-wrap gap-1.5 bg-black/40 border border-white/5 p-1 rounded-xl">
          {(['visitor', 'client', 'admin', 'superadmin'] as const).map((role) => (
            <button
              key={role}
              onClick={() => setCurrentRole(role)}
              className={`px-3 py-1.5 rounded-lg text-[9px] font-mono tracking-wider uppercase font-bold cursor-pointer transition-all ${
                currentRole === role
                  ? 'bg-gradient-to-r from-pink-500 to-indigo-500 text-white shadow-[0_0_10px_rgba(232,64,243,0.3)]'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {role === 'superadmin' ? 'Super Admin' : role}
            </button>
          ))}
        </div>
      </div>

      {/* Main Suite Workspace navigation bar */}
      <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-2 bg-black/60 p-1.5 border border-white/5 rounded-2xl">
        <button
          onClick={() => setActiveSubApp('drive')}
          className={`py-3 rounded-xl font-mono text-[10.5px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 transition-all ${
            activeSubApp === 'drive'
              ? 'bg-gradient-to-r from-sky-950/70 to-indigo-950/80 border border-sky-400/40 text-sky-400 shadow-[0_0_15px_rgba(56,189,248,0.2)] font-black'
              : 'bg-transparent border border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          <HardDrive className="w-4 h-4" />
          NexoraX Drive [Cloud 102]
        </button>
        <button
          onClick={() => {
            setActiveSubApp('docs');
            const documentFile = files.find(f => f.type === 'docs');
            if (documentFile) {
              setSelectedFile(documentFile);
              setEditorContent(documentFile.content);
            }
          }}
          className={`py-3 rounded-xl font-mono text-[10.5px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 transition-all ${
            activeSubApp === 'docs'
              ? 'bg-gradient-to-r from-indigo-950/70 to-[#e840f3]/10 border border-[#e840f3]/40 text-[#f574fe] shadow-[0_0_15px_rgba(232,64,243,0.2)] font-black'
              : 'bg-transparent border border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          <FileText className="w-4 h-4" />
          Oracle Docs [Writer]
        </button>
        <button
          onClick={() => {
            setActiveSubApp('sheets');
            const sheetFile = files.find(f => f.type === 'sheets');
            if (sheetFile) {
              setSelectedFile(sheetFile);
            }
          }}
          className={`py-3 rounded-xl font-mono text-[10.5px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 transition-all ${
            activeSubApp === 'sheets'
              ? 'bg-gradient-to-r from-emerald-950/70 to-black border border-emerald-500/40 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.2)] font-black'
              : 'bg-transparent border border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Chronos Sheets [Excel]
        </button>
        <button
          onClick={() => {
            setActiveSubApp('slides');
            const slideFile = files.find(f => f.type === 'slides');
            if (slideFile) {
              setSelectedFile(slideFile);
            }
          }}
          className={`py-3 rounded-xl font-mono text-[10.5px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 transition-all ${
            activeSubApp === 'slides'
              ? 'bg-gradient-to-r from-rose-950/70 to-black border border-rose-500/40 text-rose-400 shadow-[0_0_15px_rgba(244,63,94,0.2)] font-black'
              : 'bg-transparent border border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          <PlaySquare className="w-4 h-4" />
          Empire Slides [PowerPoint]
        </button>
      </div>

      {/* Sync Status Banner */}
      <div className="relative z-10 flex items-center gap-2.5 bg-indigo-950/40 border border-indigo-900/40 p-3 rounded-xl text-[9.5px] font-mono text-cyan-300 uppercase">
        <Cpu className="w-4 h-4 text-[#e840f3] animate-pulse" />
        <span>Status Core: <strong className="text-white">{syncStatusText}</strong></span>
      </div>

      {/* Left and Right splits */}
      <div className="relative z-10 grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        
        {/* ----------------------------------------------------
            SUB-APP: NEXORAX DRIVE
            ---------------------------------------------------- */}
        {activeSubApp === 'drive' && (
          <>
            {/* Dynamic Left Panel based on selected Role */}
            {currentRole === 'visitor' && (
              <div className="xl:col-span-4 flex flex-col gap-4 border border-white/5 bg-black/60 p-5 rounded-2xl h-full text-left font-sans">
                <span className="text-[10px] font-mono text-slate-500 font-extrabold pb-2 border-b border-white/5 uppercase block tracking-wider">
                  NexoraX Directory
                </span>
                <div className="space-y-4">
                  <div className="p-3.5 bg-cyan-950/10 border border-cyan-500/20 rounded-xl space-y-1.5">
                    <span className="text-[10px] font-mono text-cyan-400 uppercase font-black tracking-wide block">Read-Only Directory</span>
                    <p className="text-[11px] text-slate-400 leading-normal">Public visitors are granted secure directory access to explore documentation archives and deployment blueprints.</p>
                  </div>
                  <div className="space-y-2">
                    <span className="text-[9.5px] font-mono uppercase text-slate-500 block text-left">Available Folders:</span>
                    <ul className="text-xs text-slate-300 space-y-1.5 font-mono text-[10px]">
                      <li className="flex items-center gap-1.5 text-slate-400">⚡ Public Resource Library</li>
                      <li className="flex items-center gap-1.5 text-slate-400">⚡ Product Guidelines & Previews</li>
                      <li className="flex items-center gap-1.5 text-slate-400">⚡ Final Launch QA Checklist</li>
                    </ul>
                  </div>
                  <div className="pt-4 border-t border-white/5">
                    <span className="text-[9.5px] font-mono uppercase text-slate-500 block mb-2">Need Custom Integration?</span>
                    <button className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-[10px] text-white font-mono font-bold rounded-xl tracking-wider uppercase cursor-pointer">
                      Get Started with NexoraX
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentRole === 'client' && (
              <div className="xl:col-span-4 flex flex-col gap-4 border border-white/5 bg-black/60 p-5 rounded-2xl h-full text-left font-sans">
                <span className="text-[10px] font-mono text-slate-500 font-extrabold pb-2 border-b border-white/5 uppercase block tracking-wider">
                  Client Resource Vault
                </span>
                <div className="space-y-4">
                  <div className="p-3.5 bg-emerald-950/10 border border-emerald-500/20 rounded-xl space-y-1.5">
                    <span className="text-[10px] font-mono text-emerald-400 uppercase font-black tracking-wide block">Client Operations Desk</span>
                    <p className="text-[11px] text-slate-400 leading-normal">Clients have read access to custom project documents, payment balances, and active operational files.</p>
                  </div>
                  <div className="space-y-2">
                    <span className="text-[9.5px] font-mono uppercase text-slate-500 block text-left">Your Client Workspace:</span>
                    <div className="grid grid-cols-2 gap-2 text-[9px] font-mono">
                      <div className="p-2 border border-white/5 bg-black/40 rounded-lg text-slate-400 text-center">Project Files<br/><strong className="text-white">Active</strong></div>
                      <div className="p-2 border border-white/5 bg-black/40 rounded-lg text-slate-400 text-center">Invoices<br/><strong className="text-white">Cleared</strong></div>
                      <div className="p-2 border border-white/5 bg-black/40 rounded-lg text-slate-400 text-center">Usage Metrics<br/><strong className="text-white">Nominal</strong></div>
                      <div className="p-2 border border-white/5 bg-black/40 rounded-lg text-slate-400 text-center">Support Logs<br/><strong className="text-white">Stable</strong></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentRole === 'admin' && (
              <div className="xl:col-span-4 flex flex-col gap-4 border border-white/5 bg-black/60 p-4 rounded-2xl h-full">
                <span className="text-[10px] font-mono text-slate-500 font-extrabold pb-2 border-b border-white/5 uppercase block">
                  CREATE NEXORAX DOCUMENT FILE
                </span>
                <div className="space-y-4">
                  <div className="space-y-1.5 text-left">
                    <label className="text-[9px] font-mono uppercase text-slate-400">File Name (Without extension)</label>
                    <input 
                      type="text" 
                      value={newFileName}
                      onChange={(e) => setNewFileName(e.target.value)}
                      placeholder="e.g. Client_Project_Brief"
                      className="w-full bg-slate-950 border border-indigo-900 p-2.5 rounded-xl font-mono text-xs text-white"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => createNewFileInDrive('docs')}
                      className="py-2.5 bg-[#e840f3]/10 hover:bg-[#e840f3]/20 border border-[#e840f3]/30 text-[8.5px] rounded-lg font-mono font-bold text-pink-300 cursor-pointer"
                    >
                      + Word
                    </button>
                    <button
                      onClick={() => createNewFileInDrive('sheets')}
                      className="py-2.5 bg-emerald-950/30 hover:bg-emerald-900/30 border border-emerald-500/30 text-[8.5px] rounded-lg font-mono font-bold text-emerald-300 cursor-pointer"
                    >
                      + Excel
                    </button>
                    <button
                      onClick={() => createNewFileInDrive('slides')}
                      className="py-2.5 bg-rose-950/30 hover:bg-rose-900/30 border border-rose-500/30 text-[8.5px] rounded-lg font-mono font-bold text-rose-300 cursor-pointer"
                    >
                      + Slide
                    </button>
                  </div>

                  {/* NexoraX Billing Readiness */}
                  <div className="border border-indigo-950 bg-black/40 p-4 rounded-xl space-y-2 text-slate-400 text-[10px] font-mono text-left">
                    <span className="text-white font-extrabold uppercase">NexoraX Billing Readiness</span>
                    <p className="leading-relaxed">Client invoices, usage records, payment reconciliation, and accountant export tools are available inside the Admin dashboard.</p>
                    <div className="text-[9px] text-[#e840f3] font-bold">
                      Public-safe preview. No bank details displayed.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentRole === 'superadmin' && (
              <div className="xl:col-span-4 flex flex-col gap-4 border border-white/5 bg-black/60 p-4 rounded-2xl h-full">
                <span className="text-[10px] font-mono text-[#e840f3] font-extrabold pb-2 border-b border-white/5 uppercase block tracking-wider">
                  Super Admin Controls
                </span>
                
                <div className="space-y-3.5 font-mono text-[9.5px] text-slate-400 text-left">
                  <div className="p-2.5 bg-indigo-950/20 border border-indigo-900/30 rounded-xl space-y-1">
                    <span className="text-white font-extrabold block uppercase">Document System Config</span>
                    <p>Drive directory volume limit: <strong className="text-white">Unlimited</strong></p>
                    <p>Security protocol: <strong className="text-cyan-400">RSA-4096 Public Sync</strong></p>
                  </div>

                  <div className="p-2.5 bg-black border border-white/5 rounded-xl space-y-2">
                    <span className="text-[#e840f3] font-bold block uppercase">Archive Controls</span>
                    <p className="leading-tight text-slate-500">Global lock toggle overrides for active public-preview documents.</p>
                    <button 
                      onClick={() => {
                        setFiles(prev => prev.map(f => ({ ...f, isLocked: !f.isLocked })));
                        alert("Overridden archive locks across all preview catalogs!");
                      }}
                      className="w-full py-1.5 bg-indigo-950 hover:bg-indigo-900 text-indigo-300 font-bold border border-indigo-500/30 rounded cursor-pointer uppercase text-[8px]"
                    >
                      Toggle All Archive Locks
                    </button>
                  </div>

                  <div className="p-2.5 bg-black border border-white/5 rounded-xl space-y-1">
                    <span className="text-white font-extrabold block uppercase">SYSTEM AUDIT LOGS</span>
                    <div className="space-y-1 text-[8.5px] text-slate-500 font-mono">
                      <p>[13:54:10] Sync: Active Directory</p>
                      <p>[13:50:22] Cache: Cleaned legacy metrics</p>
                      <p>[13:48:15] Security: Public preview verified</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Right file listing panel */}
            <div className="xl:col-span-8 flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <span className="text-[10px] font-mono text-slate-505 font-extrabold uppercase block">
                  NexoraX Resource Library ({sortedFiles.length})
                </span>
                
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Sorting dropdown */}
                  <NexoraXSortDropdown
                    options={[
                      { value: 'priority', label: 'Node Priority' },
                      { value: 'name', label: 'Name' },
                      { value: 'lastModified', label: 'Last Modified' }
                    ]}
                    selectedValue={sortBy}
                    onSelect={(val) => setSortBy(val as 'name' | 'lastModified' | 'priority')}
                  />

                  <div className="flex items-center gap-1 bg-black/50 p-1 border border-white/5 rounded-lg">
                    <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded cursor-pointer ${viewMode === 'grid' ? "bg-[#e840f3]/20 text-[#e840f3]" : "text-slate-600 hover:text-slate-400"}`}>
                      <Grid className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`p-1.5 rounded cursor-pointer ${viewMode === 'list' ? "bg-[#e840f3]/20 text-[#e840f3]" : "text-slate-600 hover:text-slate-400"}`}>
                      <List className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {sortedFiles.length === 0 ? (
                <div className="border-2 border-dashed border-indigo-950/60 rounded-2xl p-12 text-center space-y-4 bg-black/40">
                  <span className="text-[11px] font-mono text-slate-500 block">No public resources selected yet.</span>
                  <div>
                    <button 
                      onClick={() => {
                        setFiles([
                          {
                            id: 'doc-gen-1',
                            name: 'Client_Project_Brief.docs',
                            size: '120 KB',
                            type: 'docs',
                            department: 'Public resource',
                            lastSynced: 'Just Now',
                            content: 'Custom public resource content brief draft.',
                            isLocked: false,
                            meta: 'Client Project Brief',
                            nodePriority: 'Medium',
                            lastModified: Date.now()
                          }
                        ]);
                      }}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-505 text-white rounded-xl font-mono text-[9.5px] uppercase font-bold cursor-pointer transition-all active:scale-95"
                    >
                      + Add Public Resource
                    </button>
                  </div>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {sortedFiles.map(file => (
                    <div 
                      key={file.id}
                      onClick={() => {
                        setSelectedFile(file);
                        setActiveSubApp(file.type);
                        setEditorContent(file.content);
                      }}
                      className={`group border p-4.5 rounded-2xl flex flex-col justify-between hover:bg-slate-900/40 cursor-pointer transition-all min-h-[140px] text-left relative ${
                        selectedFile?.id === file.id
                          ? 'bg-slate-950/60 border-cyan-500/80 shadow-[0_0_15px_rgba(6,182,212,0.1)]'
                          : 'bg-black/50 border-white/5'
                      }`}
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-start justify-between">
                          {file.type === 'docs' && <FileText className="w-6 h-6 text-indigo-400" />}
                          {file.type === 'sheets' && <FileSpreadsheet className="w-6 h-6 text-emerald-400" />}
                          {file.type === 'slides' && <PlaySquare className="w-6 h-6 text-rose-400" />}
                          
                          <div className="flex gap-1 items-center flex-wrap">
                            {file.nodePriority && (
                              <span className={`p-0.5 px-2 rounded-full text-[7.5px] font-mono uppercase font-black tracking-wider border ${
                                file.nodePriority === 'High' 
                                  ? 'bg-red-500/10 text-red-400 border-red-500/20' 
                                  : file.nodePriority === 'Medium' 
                                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' 
                                    : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                              }`}>
                                {file.nodePriority}
                              </span>
                            )}

                            {file.isLocked && (
                              <span className="p-0.5 rounded bg-zinc-900 border border-zinc-800 text-[8px] font-mono text-cyan-400 uppercase font-bold flex items-center gap-1">
                                <Lock className="w-2.5 h-2.5" /> VERIFIED
                              </span>
                            )}
                            
                            {!file.isLocked && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); deleteFile(file.id); }}
                                className="opacity-0 group-hover:opacity-100 p-1 bg-black border border-white/5 hover:border-red-500 rounded text-slate-500 hover:text-red-400 cursor-pointer transition-all"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <span className="text-[11.5px] font-sans font-black text-white hover:text-[#e840f3] block transition-colors leading-tight">
                            {file.name}
                          </span>
                          <span className="text-[9px] font-mono text-slate-500 block leading-tight">{file.department}</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center border-t border-white/5 pt-2.5 mt-2 text-[8px] font-mono text-slate-600">
                        <span>SIZE: {file.size}</span>
                        <span>{file.lastSynced}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-white/5 bg-black/40 rounded-2xl divide-y divide-white/5 overflow-hidden">
                  {sortedFiles.map(file => (
                    <div 
                      key={file.id} 
                      onClick={() => {
                        setSelectedFile(file);
                        setActiveSubApp(file.type);
                        setEditorContent(file.content);
                      }}
                      className="p-3.5 flex items-center justify-between hover:bg-slate-900/30 cursor-pointer transition-all"
                    >
                      <div className="flex items-center gap-3">
                        {file.type === 'docs' && <FileText className="w-4 h-4 text-indigo-400" />}
                        {file.type === 'sheets' && <FileSpreadsheet className="w-4 h-4 text-emerald-400" />}
                        {file.type === 'slides' && <PlaySquare className="w-4 h-4 text-rose-400" />}
                        
                        <div>
                          <span className="text-[11px] font-mono font-bold text-white block">{file.name}</span>
                          <span className="text-[8.5px] font-mono text-slate-500 flex items-center gap-2">
                            <span>{file.meta}</span>
                            {file.nodePriority && (
                              <span className={`px-1.5 rounded text-[7px] font-mono uppercase font-black tracking-wider border ${
                                file.nodePriority === 'High' 
                                  ? 'bg-red-500/10 text-red-400 border-red-500/20' 
                                  : file.nodePriority === 'Medium' 
                                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' 
                                    : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                              }`}>
                                {file.nodePriority}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-[9px] font-mono text-slate-500">
                        <span>{file.size}</span>
                        <span>{file.lastSynced}</span>
                        {!file.isLocked && (
                          <button onClick={(e) => { e.stopPropagation(); deleteFile(file.id); }} className="hover:text-red-400 cursor-pointer p-0.5">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ----------------------------------------------------
            SUB-APP: ORACLE DOCS [DOCS]
            ---------------------------------------------------- */}
        {activeSubApp === 'docs' && selectedFile && (
          <div className="xl:col-span-12 space-y-4">
            <div className="flex justify-between items-center border-b border-indigo-950 pb-3">
              <div>
                <span className="text-[8px] font-mono text-cyan-400 uppercase tracking-widest block font-extrabold">ORACLE DOCS GENERATOR Node</span>
                <span className="text-[12px] font-sans font-black text-white">{selectedFile.name}</span>
              </div>

              <div className="flex items-center gap-2">
                <button 
                  onClick={executeNexoraXtyAutogen}
                  className="px-3.5 py-1.5 bg-gradient-to-r from-[#e840f3] to-purple-800 text-white font-mono text-[9.5px] font-black rounded-lg cursor-pointer flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  NEXORAX AUTOGEN (100X BETTER)
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 h-full">
              {/* Main Document Body Textarea */}
              <div className="lg:col-span-8 border border-white/5 bg-[#030612]/90 rounded-2xl p-5 shadow-[0_0_20px_rgba(232,64,243,0.03)] flex flex-col gap-4">
                <textarea 
                  value={editorContent}
                  onChange={(e) => handleDocEditChange(e.target.value)}
                  className="w-full h-[380px] bg-transparent outline-none focus:outline-none border-none text-[11.5px] font-mono text-slate-100 placeholder-slate-800 leading-relaxed custom-scrollbar select-text resize-none"
                  placeholder="Initiate high-resolution document writing..."
                />
                
                <div className="border-t border-white/5 pt-3.5 mt-2 flex justify-between items-center text-[9px] font-mono text-slate-500">
                  <span>CHARACTERS: {editorContent.length}</span>
                  <span>TEMPLATES APPROVED: NexoraX Public Resource Guides</span>
                </div>
              </div>

              {/* Sidebar Action Block */}
              <div className="lg:col-span-4 border border-indigo-950/40 bg-black/60 p-4.5 rounded-2xl flex flex-col justify-between text-slate-400 text-[10px] font-mono gap-4">
                <div className="space-y-3.5">
                  <span className="text-white font-extrabold uppercase">COGNITIVE COMPLIANCE INSTRUCTIONS</span>
                  <p>Oracle Docs operates entirely locally with custom NexoraX macro expansions. There is zero reliance on US big tech cloud providers such as Microsoft Office 365 or Google Docs.</p>
                  
                  <div className="border border-white/5 p-3.5 rounded-xl bg-slate-950 space-y-2">
                    <span className="text-[#e840f3] font-bold block uppercase">VERIFIED LEDGER ASSIGNMENT</span>
                    <p>Scotland SC887095 protocols are verified. Daily Gross Yield splits track with 1% charities allocation splits immediately on document sync.</p>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t border-white/5">
                  <span className="text-slate-500 uppercase block">DIRECT ACTIONS:</span>
                  <button 
                    onClick={() => {
                      const el = document.createElement("a");
                      const file = new Blob([editorContent], { type: 'text/plain' });
                      el.href = URL.createObjectURL(file);
                      el.download = selectedFile.name;
                      document.body.appendChild(el);
                      el.click();
                      document.body.removeChild(el);
                    }}
                    className="w-full py-2 bg-indigo-950 hover:bg-indigo-900 text-indigo-300 border border-indigo-500/30 rounded-lg cursor-pointer flex items-center justify-center gap-1"
                  >
                    <Download className="w-3.5 h-3.5" />
                    DOWNLOAD DIRECTIVE [.docs]
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ----------------------------------------------------
            SUB-APP: CHRONOS SHEETS [SHEETS]
            ---------------------------------------------------- */}
        {activeSubApp === 'sheets' && selectedFile && (
          <div className="xl:col-span-12 space-y-4">
            <div className="flex justify-between items-center border-b border-indigo-950 pb-3">
              <div>
                <span className="text-[8px] font-mono text-emerald-400 uppercase tracking-widest block font-extrabold">CHRONOS QUANTUM LEDGER MATRIX</span>
                <span className="text-[12px] font-sans font-black text-white">{selectedFile.name} (NexoraX Excel Engine)</span>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <p className="text-[10px] font-mono text-slate-500">
                &gt; REAL-TIME ACCOUNTING GRID 100X MORE COMPLIANT THAN EXCEL. Direct Secure Payment Provider integration (Sort 04-**-** // Acc ******15) with auto calculation blocks.
              </p>

              <div className="border border-white/5 bg-slate-950 rounded-2xl overflow-x-auto custom-scrollbar">
                <table className="w-full text-left font-mono text-[10.5px] border-collapse min-w-[650px]">
                  <thead>
                    <tr className="bg-slate-900 border-b border-white/10">
                      <th className="p-3 text-[#e840f3] uppercase tracking-wider font-extrabold">Parameter Sector</th>
                      <th className="p-3 text-cyan-400 uppercase tracking-wider font-extrabold">Ledger Value (Editable)</th>
                      <th className="p-3 text-slate-400 uppercase tracking-wider font-extrabold">Allocated Routing Deck</th>
                      <th className="p-3 text-slate-500 uppercase text-center">Protocol Integrity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {parsedSheetData.map((row, rIdx) => (
                      <tr key={rIdx} className="hover:bg-slate-905/30 transition-colors">
                        <td className="p-3 font-semibold text-white select-none">{row[0]}</td>
                        <td className="p-1.5 min-w-[200px]">
                          <input 
                            type="text" 
                            value={row[1] || ''}
                            onChange={(e) => handleCellChange(rIdx, 1, e.target.value)}
                            className="bg-black/60 border border-white/5 focus:border-[#e840f3] rounded-lg p-2.5 w-full text-slate-100 font-mono text-[10.5px]"
                          />
                        </td>
                        <td className="p-1.5 min-w-[250px]">
                          <input 
                            type="text" 
                            value={row[2] || ''}
                            onChange={(e) => handleCellChange(rIdx, 2, e.target.value)}
                            className="bg-black/40 border border-transparent hover:border-slate-800 rounded-lg p-2.5 w-full text-slate-400 font-mono text-[10px]"
                          />
                        </td>
                        <td className="p-3 text-center">
                          <span className="inline-block w-2 w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Auto computation ledger metrics */}
              <div className="border border-indigo-950 bg-black p-4 rounded-xl flex flex-col md:flex-row justify-between gap-4 text-[9px] font-mono text-slate-400">
                <div className="space-y-1">
                  <span className="text-white uppercase font-black">NEXORAX CALCULATED EXTRACTION</span>
                  <p>Our autonomous ledger blocks monitor and verify that all inward transactions execute with the tiered Zakat (1% to Dunfermline, UK, and Global charities) and 10% Maintenance & Hosting Fee (Gavel audit).</p>
                </div>
                <div className="shrink-0 flex items-center">
                  <span className="text-[12.5px] font-black text-emerald-400 bg-emerald-950/20 px-3 py-1.5 rounded-lg border border-emerald-500/20">
                    STATUS: AUDIT INTEGRITY PASSED
                  </span>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ----------------------------------------------------
            SUB-APP: EMPIRE SLIDES [SLIDES]
            ---------------------------------------------------- */}
        {activeSubApp === 'slides' && selectedFile && (
          <div className="xl:col-span-12 space-y-4">
            <div className="flex justify-between items-center border-b border-indigo-950 pb-3">
              <div>
                <span className="text-[8px] font-mono text-rose-400 uppercase tracking-widest block font-extrabold">EMPIRE ANIMATED PRESENTER DECK</span>
                <span className="text-[12px] font-sans font-black text-white">{selectedFile.name} (NexoraX Presentation Server)</span>
              </div>
            </div>

            <p className="text-[10px] font-mono text-slate-500">
              &gt; 100X STRONGER CONVERSION RATIOS THAN POWERPOINT. Highly polished marketing templates designed to deploy deals instantly.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Left Slide outline selection */}
              <div className="lg:col-span-4 flex flex-col gap-2.5 border border-white/5 bg-black/60 p-3 rounded-2xl max-h-[350px] overflow-y-auto custom-scrollbar">
                <span className="text-[8px] font-mono text-slate-500 uppercase tracking-wider block mb-1">Slide Index Grid</span>
                {slides.map((slide, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSlideIndex(idx)}
                    className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                      activeSlideIndex === idx
                        ? 'bg-rose-950/20 border-rose-500/60 text-rose-300'
                        : 'bg-black/40 border-zinc-900 text-slate-400 hover:text-white'
                    }`}
                  >
                    <div className="text-[10.5px] font-mono font-black uppercase">
                      SLIDE {idx + 1}: {slide.title}
                    </div>
                  </button>
                ))}
              </div>

              {/* Right Slide Canvas Workspace area */}
              {slides[activeSlideIndex] && (
                <div className="lg:col-span-8 flex flex-col gap-4 border border-rose-500/15 bg-gradient-to-br from-black to-slate-950/60 p-6 rounded-2xl min-h-[300px] justify-between relative overflow-hidden text-left">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-full filter blur-[50px] pointer-events-none" />
                  
                  <div className="space-y-4 relative z-10 w-full">
                    <input 
                      type="text"
                      value={slides[activeSlideIndex].title}
                      onChange={(e) => handleSlideContentChange(activeSlideIndex, 'title', e.target.value)}
                      className="w-full bg-transparent border-b border-rose-950/40 text-lg font-sans font-extrabold text-white pb-1 focus:border-rose-500 outline-none"
                    />

                    <textarea
                      value={slides[activeSlideIndex].draft}
                      onChange={(e) => handleSlideContentChange(activeSlideIndex, 'draft', e.target.value)}
                      className="w-full min-h-[140px] bg-transparent outline-none focus:outline-none border-none text-[12.5px] font-mono text-slate-300 leading-relaxed custom-scrollbar select-text resize-none"
                    />
                  </div>

                  <div className="border-t border-white/5 pt-3 mt-4 flex justify-between items-center text-[8.5px] font-mono text-slate-500">
                    <span>COGNITIVE PLATFORM ROUTE: VANGUARD-01 Vetted</span>
                    <span>SLIDE STATUS: COMPLIANT WITH DIRECTOR STRATEGY</span>
                  </div>
                </div>
              )}
            </div>

            {/* Strategic Stripe and freephone visual deck footer */}
            <div className="border border-white/5 bg-zinc-950/50 p-4 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-[9px] font-mono text-slate-500">
              <div className="space-y-1">
                <span className="text-white font-extrabold uppercase">STRIKE ENGINE CONVERSION ROUTING PROTOCOL</span>
                <p>Slides with pitches must link to Stripe to ensure immediate settlement: <strong className="text-white underline">https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00</strong></p>
              </div>
              <div className="shrink-0 flex flex-col gap-1 text-right text-[8px]">
                <span>Voice Portal Anchor: <strong>08000 622827</strong></span>
                <span>Director direct routing line: <strong>07359******</strong></span>
              </div>
            </div>

          </div>
        )}

      </div>

      {/* Floating Plus Action Button */}
      <div className="absolute bottom-6 right-6 z-40 group">
        {/* Glow behind */}
        <div className="absolute inset-0 bg-[#e840f3] rounded-full blur-md opacity-30 group-hover:opacity-60 transition-opacity duration-300" />
        <button
          onClick={() => {
            setQTaskErrorMessage('');
            setQTaskSuccessMessage('');
            setQTaskTitle('');
            setQTaskDesc('');
            setIsQuickTaskModalOpen(true);
          }}
          className="relative bg-gradient-to-r from-cyan-500 via-[#e840f3] to-indigo-600 text-white p-3.5 rounded-full shadow-lg hover:shadow-[#e840f3]/30 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center border border-white/20"
          id="btn-workspace-quick-task"
          title="Quick Task Entry to Matrix"
        >
          <Plus className="w-5 h-5 animate-[pulse_3s_infinite]" />
        </button>
        
        {/* Tooltip */}
        <span className="absolute right-14 top-1/2 -translate-y-1/2 bg-black/90 border border-indigo-500/30 text-[9px] font-mono text-cyan-400 px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl">
          ⚡ QUICK TASK MATRIX ADD
        </span>
      </div>

      {/* Quick Task Modal */}
      <AnimatePresence>
        {isQuickTaskModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fadeIn" id="quick-task-modal-overlay">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="relative w-full max-w-lg bg-[#040817] border-2 border-indigo-500/40 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(232,64,243,0.15)] flex flex-col max-h-[90vh]"
              id="quick-task-modal"
            >
              {/* Header border decor */}
              <div className="h-1 bg-gradient-to-r from-cyan-500 via-[#e840f3] to-indigo-500" />
              
              {/* Header */}
              <div className="p-6 border-b border-white/5 bg-zinc-950/40 relative">
                <button 
                  onClick={() => setIsQuickTaskModalOpen(false)}
                  className="absolute top-5 right-5 text-slate-400 hover:text-white cursor-pointer hover:bg-white/5 p-1.5 rounded-lg transition-colors border border-white/5 text-xs font-mono"
                  id="close-quick-task-modal"
                >
                  ✕
                </button>
                <div className="space-y-1 text-left">
                  <span className="text-[9px] font-mono font-bold text-slate-500 block italic leading-none">
                    {praiseText}
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <Sparkles className="w-4 h-4 text-[#e840f3]" />
                    <h3 className="text-base font-sans font-black text-white uppercase tracking-wider">
                      Quick Matrix Todo Addition
                    </h3>
                  </div>
                  <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-tight">
                    Direct SQLite database payload registration node
                  </p>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar select-text flex-1">
                {qTaskSuccessMessage ? (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 text-center space-y-3" id="qtask-success-box">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto text-emerald-400">
                      <CheckCircle2 className="w-6 h-6 animate-bounce" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-sans font-extrabold text-white uppercase tracking-wider">Task Successfully Registered</h4>
                      <p className="text-[10px] font-mono text-emerald-400">{qTaskSuccessMessage}</p>
                    </div>
                    <p className="text-[9px] font-mono text-slate-500 uppercase">Synchronous replication to Chronos Ledger Node active.</p>
                  </div>
                ) : (
                  <form onSubmit={handleQuickTaskSubmit} className="space-y-4 text-left" id="qtask-form">
                    
                    {qTaskErrorMessage && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-[10px] font-mono text-rose-400 flex items-center gap-2" id="qtask-error-box">
                        <ShieldAlert className="w-4 h-4 shrink-0" />
                        <span>{qTaskErrorMessage}</span>
                      </div>
                    )}

                    {/* Title */}
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                        Task Subject (Required)
                      </label>
                      <input 
                        type="text"
                        required
                        value={qTaskTitle}
                        onChange={(e) => setQTaskTitle(e.target.value)}
                        placeholder="e.g. Audit SC887095 liquidity parameters"
                        className="w-full bg-black/50 border border-indigo-950/80 focus:border-cyan-500/50 rounded-xl px-4 py-2.5 text-xs text-white outline-none placeholder-slate-600 transition-colors"
                        id="qtask-input-title"
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                        Detailed Parameters / Scope
                      </label>
                      <textarea 
                        value={qTaskDesc}
                        onChange={(e) => setQTaskDesc(e.target.value)}
                        placeholder="Define operational requirements and target deliverables..."
                        className="w-full h-20 bg-black/50 border border-indigo-950/80 focus:border-cyan-500/50 rounded-xl px-4 py-2 text-xs text-white outline-none placeholder-slate-600 transition-colors custom-scrollbar resize-none"
                        id="qtask-input-desc"
                      />
                    </div>

                    {/* Grid of options: Priority & Category */}
                    <div className="grid grid-cols-2 gap-4">
                      
                      {/* Priority */}
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                          Priority Rank
                        </label>
                        <select
                          value={qTaskPriority}
                          onChange={(e) => setQTaskPriority(e.target.value)}
                          className="w-full bg-black/50 border border-indigo-950/80 focus:border-cyan-500/50 rounded-xl px-3 py-2 text-xs text-white outline-none cursor-pointer transition-colors"
                          id="qtask-input-priority"
                        >
                          <option value="LOW">🔵 LOW PRIORITY</option>
                          <option value="MEDIUM">🟢 MEDIUM PRIORITY</option>
                          <option value="HIGH">🟡 HIGH PRIORITY</option>
                          <option value="URGENT">🔴 URGENT REQUIREMENT</option>
                        </select>
                      </div>

                      {/* Category */}
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                          Sector Category
                        </label>
                        <select
                          value={qTaskCategory}
                          onChange={(e) => setQTaskCategory(e.target.value)}
                          className="w-full bg-black/50 border border-indigo-950/80 focus:border-cyan-500/50 rounded-xl px-3 py-2 text-xs text-white outline-none cursor-pointer transition-colors"
                          id="qtask-input-category"
                        >
                          <option value="CORE">🧠 CORE SYSTEM</option>
                          <option value="FINANCIAL">💰 FINANCIAL ACC</option>
                          <option value="COMPLIANCE">⚖️ COMPLIANCE</option>
                          <option value="SYSTEM">🌐 VANGUARD NODE</option>
                          <option value="SECURE">🔒 SECURE LEDGER</option>
                        </select>
                      </div>

                    </div>

                    {/* Grid of parameters: Due Date & Hardware Allocation */}
                    <div className="grid grid-cols-2 gap-4">
                      
                      {/* Due Date */}
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                          Target Due Date
                        </label>
                        <input 
                          type="date"
                          value={qTaskDueDate}
                          onChange={(e) => setQTaskDueDate(e.target.value)}
                          className="w-full bg-black/50 border border-indigo-950/80 focus:border-cyan-500/50 rounded-xl px-3 py-2 text-xs text-white outline-none transition-colors"
                          id="qtask-input-duedate"
                        />
                      </div>

                      {/* Hardware Allocation */}
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                          Intel Core Allocation
                        </label>
                        <select
                          value={qTaskHardware}
                          onChange={(e) => setQTaskHardware(e.target.value)}
                          className="w-full bg-black/50 border border-indigo-950/80 focus:border-cyan-500/50 rounded-xl px-3 py-2 text-xs text-white outline-none cursor-pointer transition-colors"
                          id="qtask-input-hardware"
                        >
                          <option value="Cores 0-1">C0-C1 // Low Telemetry</option>
                          <option value="Cores 0-3">C0-C3 // Balance Allocation</option>
                          <option value="Cores 0-7">C0-C7 // Max Speed Threading</option>
                          <option value="Vanguard Hub">Vanguard Hub Cluster (90% Thread)</option>
                        </select>
                      </div>

                    </div>

                    {/* Sliders and Telemetry Hook */}
                    <div className="bg-black/40 border border-white/5 p-4 rounded-xl space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-mono text-slate-400 uppercase font-bold">Telemetry Weight Burden: <strong>{qTaskTelemetry}%</strong></span>
                        <span className="text-[8px] font-mono text-slate-500">MAX 100% HOST THREAD</span>
                      </div>
                      <input 
                        type="range"
                        min="5"
                        max="100"
                        value={qTaskTelemetry}
                        onChange={(e) => setQTaskTelemetry(Number(e.target.value))}
                        className="w-full h-1 bg-indigo-950 rounded-lg appearance-none cursor-pointer accent-[#e840f3]"
                        id="qtask-input-telemetry"
                      />
                      <div className="flex justify-between text-[7.5px] font-mono text-slate-600">
                        <span>5% STEADY</span>
                        <span>50% NOMINAL</span>
                        <span>100% MAXIMUM</span>
                      </div>
                    </div>

                    {/* Action Hook Script */}
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold">
                        Autonomous Action Hook Script (Bash)
                      </label>
                      <input 
                        type="text"
                        value={qTaskHook}
                        onChange={(e) => setQTaskHook(e.target.value)}
                        placeholder="echo 'Trigger task hook'"
                        className="w-full bg-black/40 border border-indigo-950/80 focus:border-[#e840f3]/50 rounded-xl px-3 py-1.5 text-[10.5px] font-mono text-indigo-300 outline-none placeholder-slate-700 transition-colors"
                        id="qtask-input-hook"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
                      <button
                        type="button"
                        onClick={() => setIsQuickTaskModalOpen(false)}
                        className="px-4 py-2 bg-black hover:bg-zinc-900 border border-white/5 hover:border-white/10 text-[10.5px] font-mono text-slate-300 rounded-xl cursor-pointer transition-colors"
                        id="btn-qtask-cancel"
                      >
                        ABORT ENTRY
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmittingQTask}
                        className="relative px-5 py-2 bg-gradient-to-r from-cyan-500 via-[#e840f3] to-indigo-600 hover:from-cyan-400 hover:via-indigo-500 hover:to-indigo-500 text-white text-[10.5px] font-mono font-bold uppercase rounded-xl cursor-pointer flex items-center gap-1.5 hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_15px_rgba(232,64,243,0.2)] disabled:opacity-50"
                        id="btn-qtask-submit"
                      >
                        {isSubmittingQTask ? 'PROCESSING...' : 'AUTHORIZE INJECTION'}
                      </button>
                    </div>

                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
